import {
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";
import { Typography } from "@platform/design-system";
import { useCallback, useEffect, useState } from "react";

import { SelectedCoverage, FormState } from "../types";

import { isDuplicate } from "./utils";

/**
 * Keys the class options by their text, suffixing repeats so duplicate class
 * names still get distinct keys without falling back to the index.
 */
// Class names are user-provided and may repeat, so the key pairs the name with
// its occurrence count in an encoding no name can forge.
export const keyedByText = (
  values: readonly string[],
): { key: string; value: string }[] =>
  values.map((value, i) => {
    const repeats = values.slice(0, i).filter((v) => v === value).length;
    return { key: JSON.stringify([value, repeats]), value };
  });

interface CensusClassesSelectProps {
  options: string[];
  selectedCoverages: SelectedCoverage;
  coverageName: string;
  updateFormStateValue: <T extends keyof FormState>(
    key: T,
    value: FormState[T],
  ) => void;
}

const CensusClassesSelect = (
  props: CensusClassesSelectProps,
): React.JSX.Element => {
  const { options, coverageName, selectedCoverages, updateFormStateValue } =
    props;
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);

  const updateSelectedCoverages = useCallback(
    (newSelectedClasses: string[]) => {
      const sortedValue = newSelectedClasses.sort(
        (a, b) => options.indexOf(a) - options.indexOf(b),
      );
      setSelectedClasses(sortedValue);

      const newSelectedCoverages = {
        ...selectedCoverages,
        [coverageName]: {
          ...selectedCoverages[coverageName],
          coverClasses: sortedValue,
        },
      };
      updateFormStateValue("selectedCoverages", newSelectedCoverages);
    },
    [options, selectedCoverages, coverageName, updateFormStateValue],
  );

  // Drop selections whose class was renamed or removed. The early return keeps
  // the pruning write from re-triggering itself through the parent re-render.
  useEffect(() => {
    const stillOffered = selectedClasses.filter((cls) => options.includes(cls));
    if (stillOffered.length === selectedClasses.length) return;

    updateSelectedCoverages(stillOffered);
  }, [options, selectedClasses, updateSelectedCoverages]);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string[];
    setSelectedClasses(value);

    updateSelectedCoverages(value);
  };

  const areAllOptionsEmpty = options.every((option) => option.trim() === "");

  return (
    <>
      <FormLabel sx={{ marginBottom: 2 }} required>
        Which classes are eligible for 100% employer paid coverage?
      </FormLabel>
      <FormControl fullWidth>
        <InputLabel id="employee-classes-label" required>
          Employee Classes
        </InputLabel>
        <Select
          labelId="employee-classes-label"
          id="employee-classes-select"
          value={selectedClasses}
          label="Employee Classes"
          multiple
          required
          onChange={handleChange}
          renderValue={(selected) =>
            (selected as string[])
              .map(
                (selectedValue) =>
                  `Class ${options.indexOf(selectedValue) + 1}`,
              )
              .join(", ")
          }
          MenuProps={{ disablePortal: true }}
        >
          {areAllOptionsEmpty ? (
            <MenuItem disabled>
              <Typography>
                <em>No Classes added in Company Details section</em>
              </Typography>
            </MenuItem>
          ) : (
            keyedByText(options).map(({ key, value }, index) => (
              <MenuItem
                key={key}
                value={value}
                disabled={value === "" || isDuplicate(options, value, index)}
              >
                <Checkbox checked={selectedClasses.includes(value)} />
                <ListItemText primary={`Class ${index + 1} - ${value}`} />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </>
  );
};

export default CensusClassesSelect;
